import React, { useEffect, useMemo, useState } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import Button from '../../../components/common/Button';
import Table from '../../../components/common/Table';
import { useNavigate, useParams } from 'react-router-dom';
import { useLightweightCompetitionResultEntries } from '../../../hooks/useCompetitionResultEntryApi';
import { CompetitionResultEntryDTO } from '../../../services/apiClient';

interface ExerciseGroup {
  competitionExerciseId: string;
  exerciseType: string;
  title: string;
  className: string;
  entryCount: number;
}

const CompetitionResultExercises: React.FC = () => {
  const { id: competitionResultId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getLightweightEntriesForResult, isLoading } = useLightweightCompetitionResultEntries();
  const [entries, setEntries] = useState<CompetitionResultEntryDTO[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Competition Results', href: '/admin/competitionresults' },
    { label: 'Exercises', href: '#' },
  ];

  useEffect(() => {
    const fetchEntries = async () => {
      if (!competitionResultId) return;
      const results = await getLightweightEntriesForResult(competitionResultId);
      setEntries(results);
    };

    fetchEntries();
  }, [competitionResultId]);

  const exerciseGroups = useMemo<ExerciseGroup[]>(() => {
    const groups = new Map<string, ExerciseGroup>();

    entries.forEach((entry) => {
      const exercise = entry.competitionExercise;
      if (!exercise?.id) return;

      const existing = groups.get(exercise.id);
      if (existing) {
        existing.entryCount += 1;
        return;
      }

      groups.set(exercise.id, {
        competitionExerciseId: exercise.id,
        exerciseType: exercise.competitionExerciseType?.title || '-',
        title: exercise.exerciseSpecificTitle || exercise.exerciseSpecific?.title || '-',
        className: `${exercise.competitionClassType?.title || '-'} - ${exercise.gender?.title || '-'}`,
        entryCount: 1,
      });
    });

    return Array.from(groups.values()).sort((a, b) => a.title.localeCompare(b.title));
  }, [entries]);

  useEffect(() => {
    setCurrentPage(1);
  }, [exerciseGroups.length]);

  const totalPages = Math.max(1, Math.ceil(exerciseGroups.length / pageSize));
  const pagedExerciseGroups = exerciseGroups.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );

  const competitionName = entries[0]?.competitionResult?.competition?.name;

  return (
    <AdminLayout
      activeMenuItem="/admin/competitionresults"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
      requiredPermission="Manage.CompetitionResult.View"
    >
      <Card
        title={`Exercises for: ${competitionName || 'Competition Result'}`}
        headerActions={
          <Button variant="secondary" onClick={() => navigate('/admin/competitionresults')}>
            Back
          </Button>
        }
      >
        <Table
          columns={[
            { label: 'Exercise', key: 'exerciseType' },
            { label: 'Title', key: 'title' },
            { label: 'Class', key: 'className' },
            { label: 'Entries', key: 'entryCount' },
          ]}
          data={pagedExerciseGroups}
          actions={{
            onEdit: (row: ExerciseGroup) =>
              navigate(
                `/admin/competitionresults/${competitionResultId}/exercises/${row.competitionExerciseId}`,
              ),
          }}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          isLoading={isLoading}
          noDataMessage="No exercises found for this competition result."
        />
      </Card>
    </AdminLayout>
  );
};

export default CompetitionResultExercises;
